import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Fade,
	FormControlLabel,
	MobileStepper,
	Stack,
	Step,
	StepLabel,
	Stepper,
	Tab,
	Tabs,
	useMediaQuery
} from '@mui/material';
import GeneralInformationStep from './Stepper/GeneralInformationStep';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@emotion/react';
import DescriptionStep from './Stepper/DescriptionStep';
import ConfigurationSettings from './Stepper/ConfigurationSettings';
import { Form, Formik } from 'formik';
import ConfirmStep from './Stepper/ConfirmStep';
import v1Validate from 'pages/publishApp/helpers/v1Validate';
import { useAuthUser } from '@tenant/utility/AuthHooks';
import { APP_CONNECTIVITY_STATE_ENUM, PLATFORM_ENUM } from '@tenant/utility/constants/enum';
import useModal from '@tenant/utility/hooks/useModal';
import { LoadingButton } from '@mui/lab';
import { validatePublishApp } from 'pages/listApp/helpers/validate';
import AppVersion from 'pages/listApp/components/PublishApp/AppVersion';
import { ACTION_MODE_ENUM, StatusAppType } from 'constants/AppEnums';
import isEmpty from 'lodash/isEmpty';
import AppVersionLog from 'pages/listApp/components/PublishApp/AppVersionLog';
import PricingStep from './Stepper/PricingStep';
import PermissionConfigurationStep from './Stepper/PermissionConfigurationStep';
import { useNavigate } from 'react-router-dom';
import { grey } from '@mui/material/colors';
import { STEPS_CONFIG } from './constants';
import { checkAllowedAccess } from '@tenant/core/AppPermission/checkAllowedAccess.helper';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ROUTE_STRING } from 'constants/RouteString';

const STEP_KEY_HASH = [STEPS_CONFIG.GENERAL.key, STEPS_CONFIG.DESCRIPTION.key, STEPS_CONFIG.CONFIGURATION.key, STEPS_CONFIG.PRICING.key, STEPS_CONFIG.PERMISSION_CONFIGURATION.key];

const Steps = ({ mode, input, submit, isPending, refetch }) => {
	const navigate = useNavigate();
	const editMode = mode === ACTION_MODE_ENUM['Edit'];
	const theme = useTheme();
	const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
	const { permissions } = useSelector(state => state.authorization);

	const steps = editMode
		? [STEPS_CONFIG.GENERAL, STEPS_CONFIG.DESCRIPTION, STEPS_CONFIG.CONFIGURATION, STEPS_CONFIG.PRICING, STEPS_CONFIG.PERMISSION_CONFIGURATION].filter(step => checkAllowedAccess(permissions, step.permissionIds))
		: [STEPS_CONFIG.GENERAL, STEPS_CONFIG.DESCRIPTION, STEPS_CONFIG.CONFIGURATION, STEPS_CONFIG.PERMISSION_CONFIGURATION, STEPS_CONFIG.CONFIRMATION];

	const [activeStep, setActiveStep] = useState(() => {
		const hash = window.location.hash.replace('#', '');
		const stepIndex = STEP_KEY_HASH.indexOf(hash);
		return stepIndex !== -1 ? STEP_KEY_HASH[stepIndex] : STEPS_CONFIG.GENERAL.key;
	});
	const [submitValue, setSubmitValue] = useState();
	const [tempStep, setTempStep] = useState();
	const [keyResetForm, setKeyResetForm] = useState(0);
	const [initialValues, setInitialValues] = useState();
	const [disabledConfirm, setDisabledConfirm] = useState(false);
	const [isModuleIdChanged, setIsModuleIdChanged] = useState(false);

	const { user } = useAuthUser();
	const { visible: modalCreateVisible, onShow: onShowModalCreate, onClose: onCloseModalCreate } = useModal();
	const { visible: modalEditVisible, onShow: onShowModalEdit, onClose: onCloseModalEdit } = useModal();
	const { visible: modalUnsavedChanges, onShow: onShowModalUnsavedChanges, onClose: onCloseModalUnsavedChanges } = useModal();

	const isPricingStep = useMemo(() => {
		return activeStep === STEPS_CONFIG.PRICING.key && editMode;
	}, [activeStep, editMode]);

	const updateInitialValues = useCallback(
		newInitialValues => {
			setInitialValues({
				...input,
				...newInitialValues
			});
		},
		[input]
	);

	const handleNext = () => {
		const findIndex = steps.findIndex(e => e.key === activeStep);
		const newKey = findIndex > -1 ? steps[findIndex + 1]?.key : undefined;
		if (!newKey) {
			return;
		}
		setActiveStep(newKey);
	};

	const handleBack = () => {
		const findIndex = steps.findIndex(e => e.key === activeStep);
		const newKey = findIndex > -1 ? steps[findIndex - 1]?.key : undefined;
		if (!newKey) {
			return;
		}
		setActiveStep(newKey);
	};

	const handleSubmitEdit = values => {
		handleSubmit({
			...submitValue,
			...values,
			IsPrivate: !values.IsPublic,
			PersonasIds: values.Personas?.map(e => e.Id) ?? []
		});
		setKeyResetForm(s => s + 1);
	};

	const transformMetaDataSubmit = data => {
		if (!data) return [];

		try {
			const isValidFeatureItem = feature => {
				return feature.source && feature.featureName;
			};
			return data
				.map(entry => {
					const slots = entry.slots.reduce((acc, slot) => {
						let listFeature =
							slot.features?.map(feature => {
								const result = {
									...feature
								};

								if (['slotAdditionMenu', 'slotTab'].includes(slot.type)) {
									result.extraData = {
										tag: 'li'
									};
								}

								return result;
							}) ?? [];

						listFeature = listFeature.filter(feature => isValidFeatureItem(feature));

						if (listFeature?.length) {
							acc[slot.type] = listFeature;
						}

						return acc;
					}, {});

					return {
						...entry,
						slots
					};
				})
				.filter(entry => !isEmpty(entry.slots));
		} catch (e) {
			console.error(e);
			return [];
		}
	};

	const handleSubmit = values => {
		if (!editMode && steps.findIndex(e => e.key === activeStep) < steps.length - 1) {
			handleNext();
			return;
		}

		const data = {
			...values,
			MetaData: JSON.stringify(transformMetaDataSubmit(values.PageSlots), null, 2),
			PortalMetaData: JSON.stringify(transformMetaDataSubmit(values.PageSlotsPortal), null, 2),
			CreatedBy: user.localAccountId,
			IconFile: undefined,
			CategoryIds: values.CategoryIds.map(e => e.Id),
			IconBlobReference: values.IconFile,
			IsPrivate: !values.IsPublic,
			PersonasIds: values.Personas?.map(e => e.Id) ?? []
		};

		delete data.PageSlots;
		delete data.PageSlotsPortal;
		delete data.PageSlotsPortalTemp;

		if (!isPricingStep) {
			delete data.PricingPlan;
			delete data.IsMultiTierEnabled;
		}

		// Validate this one only in case Create app
		if (mode === ACTION_MODE_ENUM['Create']) {
			if (data.AppVersion) delete data.AppVersion;
			if (data.AppVersionNumber) delete data.AppVersionNumber;
		}

		// For mobile, check app connectivity state to remove un-use content
		const isSupportMobile = values.Platform === PLATFORM_ENUM['Mobile'] || values.Platform === PLATFORM_ENUM['Hybrid'];

		if (isSupportMobile) {
			const appConnectivityState = values.AppConnectivityState;
			if (appConnectivityState === APP_CONNECTIVITY_STATE_ENUM['Online']) {
				data.MobileAppUrl = values.MobileFeatureUrl;
				delete data.MobileFeatureUrl;
			}
		}

		const formData = new FormData();

		formData.append('IconBlobReference', data.IconBlobReference || null);
		formData.append('BannerBlobReference', data.BannerFile || null);

		submit(
			isPricingStep
				? {
					Pricings: values.PricingPlan,
					IsMultiTierEnabled: values.IsMultiTierEnabled
				}
				: {
					...data,
					AppIconFile: formData.get('IconBlobReference'),
					AppBannerFile: formData.get('BannerBlobReference'),
					CompanyProducts: mode === ACTION_MODE_ENUM['Create'] ? values.CompanyProducts?.map(e => e.value) ?? [] : values.CompanyProducts
				},
			{
				activeStep,
				onSuccess: async data => {
					if (editMode) {
						onCloseModalCreate();
						onCloseModalEdit();
						await refetch?.();
						setKeyResetForm(s => s + 1);
						return;
					}

					toast.success('Your app has been successfully created.');
					navigate(ROUTE_STRING.LIST_APP.LIST);
				}
			}
		);
	};

	useEffect(() => {
		window.location.hash = STEP_KEY_HASH[activeStep] || '';
	}, [activeStep]);

	useEffect(() => {
		if (!editMode) return;
		const currentStepsKey = steps.map(step => step.key);
		if (!currentStepsKey.includes(activeStep) && currentStepsKey.length > 0) {
			setActiveStep(currentStepsKey[0]);
		}
	}, []);

	return (
		<>
			<Dialog fullWidth maxWidth="sm" open={modalCreateVisible} onClose={onCloseModalCreate} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
				<DialogTitle id="alert-dialog-title" fontSize={20} fontWeight={600}>
					Confirm Submission
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description" fontSize={13} mb={5}>
						You&apos;re about to submit your app. Please review all updates carefully before proceeding. Once submitted, it will take effect and may impact your
						app&apos;s functionality or users. Do you want to continue?
					</DialogContentText>

					{isModuleIdChanged && (
						<>
							<Box mb={5}>
								<Alert
									severity="warning"
									color="warning"
									icon={false}
									sx={{
										backgroundColor: '#fff4e5'
									}}
								>
									Warning! We&apos;ve detected that the <b>Module Id</b> has been modified. Modifying the <b>Module Id</b> can have serious consequences,
									potentially affecting previously granted or denied permissions for customers subscribed to this app, especially if the module has associated
									permissions.
								</Alert>
							</Box>

							<FormControlLabel
								sx={{ '.MuiTypography-body1': { fontSize: 12 } }}
								control={<Checkbox checked={!disabledConfirm} />}
								label="I fully agree to the update and take complete responsibility for all actions and consequences arising from this decision."
								onChange={e => setDisabledConfirm(!e.target.checked)}
							/>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<LoadingButton variant="contained" color="secondary" disabled={isPending} onClick={onCloseModalCreate}>
						Cancel
					</LoadingButton>
					<LoadingButton disabled={disabledConfirm} loading={isPending} onClick={() => handleSubmit(submitValue)} variant="contained">
						Confirm & Submit
					</LoadingButton>
				</DialogActions>
			</Dialog>

			<Dialog maxWidth="sm" fullWidth open={modalEditVisible} onClose={onCloseModalEdit}>
				<DialogTitle sx={{ fontSize: 20 }}>Are you sure you want to edit this app?</DialogTitle>
				<DialogContent>
					<Formik
						initialValues={{
							AppVersion: submitValue?.AppVersion,
							Platform: submitValue?.Platform,
							MobileAppVersion: submitValue?.MobileAppVersion
						}}
						validationSchema={validatePublishApp}
						onSubmit={handleSubmitEdit}
					>
						{({ values, errors, setFieldValue, handleSubmit }) => (
							<Form onSubmit={handleSubmit}>
								<Stack direction="column" spacing={4}>
									{submitValue?.Platform == PLATFORM_ENUM['Web'] && (
										<>
											<AppVersion
												title={'Web App Version'}
												fieldKey={'AppVersion'}
												errorText={errors.AppVersion}
												valueText={values.AppVersion}
												setFieldValue={setFieldValue}
											/>

											<AppVersionLog
												title={'Web App Version Log'}
												fieldKey={'AppVersionLog'}
												errorText={errors.AppVersionLog}
												valueText={values.AppVersionLog}
												setFieldValue={setFieldValue}
											/>
										</>
									)}

									{submitValue?.Platform == PLATFORM_ENUM['Mobile'] && (
										<>
											<AppVersion
												title={'Mobile App Version'}
												fieldKey={'MobileAppVersion'}
												errorText={errors.MobileAppVersion}
												valueText={values.MobileAppVersion}
												setFieldValue={setFieldValue}
											/>

											<AppVersionLog
												title={'Mobile App Version Log'}
												fieldKey={'MobileAppVersionLog'}
												errorText={errors.MobileAppVersionLog}
												valueText={values.MobileAppVersionLog}
												setFieldValue={setFieldValue}
											/>
										</>
									)}

									{submitValue?.Platform == PLATFORM_ENUM['Hybrid'] && (
										<>
											<AppVersion
												title={'Web App Version'}
												fieldKey={'AppVersion'}
												errorText={errors.AppVersion}
												valueText={values.AppVersion}
												setFieldValue={setFieldValue}
											/>

											<AppVersionLog
												title={'Web App Version Log'}
												fieldKey={'AppVersionLog'}
												errorText={errors.AppVersionLog}
												valueText={values.AppVersionLog}
												setFieldValue={setFieldValue}
											/>

											<AppVersion
												title={'Mobile App Version'}
												fieldKey={'MobileAppVersion'}
												errorText={errors.MobileAppVersion}
												valueText={values.MobileAppVersion}
												setFieldValue={setFieldValue}
											/>

											<AppVersionLog
												title={'Mobile App Version Log'}
												fieldKey={'MobileAppVersionLog'}
												errorText={errors.MobileAppVersionLog}
												valueText={values.MobileAppVersionLog}
												setFieldValue={setFieldValue}
											/>
										</>
									)}
								</Stack>
								<DialogActions sx={{ mt: 5, p: 0 }}>
									<Button variant="contained" color="secondary" onClick={onCloseModalEdit}>
										Cancel
									</Button>
									<LoadingButton loading={isPending} variant="contained" color="primary" type="submit" sx={{ minWidth: 80 }}>
										Edit
									</LoadingButton>
								</DialogActions>
							</Form>
						)}
					</Formik>
				</DialogContent>
			</Dialog>

			<Formik
				key={keyResetForm}
				enableReinitialize
				validateOnChange={true}
				validateOnBlur={true}
				initialValues={isPricingStep && initialValues ? initialValues : input}
				onSubmit={data => {
					if (editMode) {
						setSubmitValue({ ...data });
						const isSupportMobile = data.Platform === PLATFORM_ENUM['Mobile'] || data.Platform === PLATFORM_ENUM['Hybrid'];
						const isShowModalEdit = mode === ACTION_MODE_ENUM['Edit'] && isSupportMobile && data.Status === StatusAppType['ACTIVE'];

						const isModuleIdChanged = mode === ACTION_MODE_ENUM['Edit'] && data.ModuleId !== input?.ModuleId;

						setIsModuleIdChanged(isModuleIdChanged);
						setDisabledConfirm(isModuleIdChanged);

						isShowModalEdit && !isModuleIdChanged ? onShowModalEdit() : onShowModalCreate();
						return;
					}

					handleSubmit(data);
				}}
				validationSchema={steps.findIndex(e => e.key === activeStep) > -1 ? v1Validate[steps.findIndex(e => e.key === activeStep)] : undefined}
			>
				{({ submitForm, values, setFieldValue, errors, touched, dirty, resetForm }) => (
					<>
						<Dialog
							fullWidth
							maxWidth="sm"
							open={modalUnsavedChanges}
							onClose={onCloseModalUnsavedChanges}
							aria-labelledby="alert-dialog-title"
							aria-describedby="alert-dialog-description"
						>
							<DialogTitle id="alert-dialog-title" fontSize={20}>
								Unsaved changes
							</DialogTitle>
							<DialogContent>
								<DialogContentText id="alert-dialog-description">You have unsaved changes that will be lost if you decide to discard.</DialogContentText>
								<DialogContentText id="alert-dialog-description-2">Are you sure you want to discard changes?</DialogContentText>
							</DialogContent>
							<DialogActions>
								<LoadingButton variant="contained" color="secondary" onClick={onCloseModalUnsavedChanges}>
									Keep Editing
								</LoadingButton>
								<LoadingButton
									onClick={() => {
										resetForm();
										setActiveStep(tempStep);
										setTempStep();
										onCloseModalUnsavedChanges();
									}}
									variant="contained"
									color="error"
									sx={{ boxShadow: 'none', fontWeight: 400 }}
								>
									Discard Changes
								</LoadingButton>
							</DialogActions>
						</Dialog>

						{editMode ? (
							<Box
								sx={{
									borderBottom: '1px solid #ddd',
									'& .MuiTabs-root': {
										mt: -4
									}
								}}
							>
								<Tabs
									value={activeStep?.toString()}
									onChange={(e, value) => {
										if (dirty && activeStep?.toString() !== STEPS_CONFIG.PERMISSION_CONFIGURATION.key) {
											setTempStep(value);
											onShowModalUnsavedChanges();
											return;
										}
										setInitialValues();
										setActiveStep(value);
									}}
								>
									{steps.map(step => (
										<Tab label={step.label} value={step.key} key={step.key} />
									))}
								</Tabs>
							</Box>
						) : (
							<Stepper
								activeStep={steps.findIndex(e => e.key === activeStep)}
								alternativeLabel
								sx={{
									pb: 2,
									borderBottom: '1px solid #ddd',
									'& .MuiStepLabel-labelContainer': {
										mt: -2
									}
								}}
							>
								{steps.map(step => (
									<Step key={step.key}>
										<StepLabel>{step.label}</StepLabel>
									</Step>
								))}
							</Stepper>
						)}
						<Box
							sx={{
								height: editMode ? 'calc(100vh - 258px)' : 'calc(100vh - 283px)',
								overflow: 'auto',
								ml: -2,
								mr: -2
							}}
						>
							<Fade in={true} timeout={300}>
								<Box p={5} mt={5}>
									{activeStep?.toString() === STEPS_CONFIG.GENERAL.key && (
										<GeneralInformationStep values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} />
									)}
									{activeStep?.toString() === STEPS_CONFIG.DESCRIPTION.key && (
										<DescriptionStep values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} keyResetForm={keyResetForm} />
									)}
									{activeStep?.toString() === STEPS_CONFIG.CONFIGURATION.key && (
										<ConfigurationSettings values={values} errors={errors} setFieldValue={setFieldValue} defaultAppCode={input?.AppCode} />
									)}
									{isPricingStep && (
										<PricingStep
											key={keyResetForm}
											keyChanged={keyResetForm}
											values={values}
											errors={errors}
											touched={touched}
											setFieldValue={setFieldValue}
											updateInitialValues={updateInitialValues}
										/>
									)}
									{activeStep?.toString() === STEPS_CONFIG.PERMISSION_CONFIGURATION.key && (
										<PermissionConfigurationStep
											values={values}
											errors={errors}
											touched={touched}
											setFieldValue={setFieldValue}
											independentMode={true}
										/>
									)}
									{!editMode && activeStep?.toString() === STEPS_CONFIG.CONFIRMATION.key && <ConfirmStep values={values} errors={errors} />}
								</Box>
							</Fade>
						</Box>
						<FooterWrapper
							className="footer"
							sx={{
								backgroundColor: '#fff',
								position: 'fixed',
								bottom: 0,
								right: 0,
								left: isLgDown ? 0 : '280px',
								borderTop: `1px solid ${grey[200]}`,
								'& .MuiButtonBase-root': {
									minWidth: '80px'
								}
							}}
						>
							{!editMode && (
								<MobileStepper
									variant="progress"
									steps={steps.length}
									position="static"
									activeStep={steps.findIndex(e => e.key === activeStep)}
									nextButton={
										steps.findIndex(e => e.key === activeStep) < steps.length - 1 ? (
											<Button size="small" onClick={submitForm}>
												Next
												{theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
											</Button>
										) : (
											<Button
												size="small"
												variant="contained"
												onClick={() => {
													if (steps.findIndex(e => e.key === activeStep) === steps.length - 1) {
														setSubmitValue({ ...values });
														// Check mode to show dialog create or edit
														const isSupportMobile = values.Platform === PLATFORM_ENUM['Mobile'] || values.Platform === PLATFORM_ENUM['Hybrid'];
														const isShowModalEdit = mode === ACTION_MODE_ENUM['Edit'] && isSupportMobile && values.Status === StatusAppType['ACTIVE'];

														if (isShowModalEdit) {
															onShowModalEdit();
														} else {
															onShowModalCreate();
														}
													}
												}}
											>
												Submit
											</Button>
										)
									}
									backButton={
										<Button size="small" onClick={handleBack} disabled={activeStep === 0}>
											{theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
											Back
										</Button>
									}
								/>
							)}

							{editMode && activeStep?.toString() !== STEPS_CONFIG.PERMISSION_CONFIGURATION.key && (
								<Box
									display="flex"
									justifyContent="end"
									alignItems="center"
									sx={{
										p: 2,
										pt: 1.5,
										'& .MuiButton-root': {
											mr: 2
										}
									}}
								>
									{dirty && (
										<Button
											variant="contained"
											color="secondary"
											onClick={() => {
												resetForm();
												setKeyResetForm(s => s + 1);
											}}
										>
											Discard
										</Button>
									)}
									<LoadingButton
										variant="contained"
										disabled={!dirty}
										onClick={() => {
											submitForm();
										}}
									>
										Save
									</LoadingButton>
								</Box>
							)}
						</FooterWrapper>
					</>
				)}
			</Formik>
		</>
	);
};

export default React.memo(Steps);

Steps.propTypes = {
	mode: PropTypes.number,
	input: PropTypes.object,
	submit: PropTypes.func,
	refetch: PropTypes.func,
	isPending: PropTypes.bool
};
